import { FormEvent, useState } from "react";
import { Link } from "react-router-dom";

import { requestPasswordReset } from "../api/auth";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await requestPasswordReset(email.trim());
      // The backend returns 200 whether or not the account exists, so
      // we always show the same "check your email" message — the user
      // doesn't get to confirm that an email is in our system.
      setSent(true);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response
        ?.data?.detail;
      setError(detail ?? "Couldn't send the reset email. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-sm">
        <h1 className="text-2xl font-semibold text-slate-900">Reset your password</h1>
        {sent ? (
          <>
            <p className="mt-3 text-slate-600">
              If an account with that email exists, we've sent password reset
              instructions. Check your inbox — the link expires in 1 hour.
            </p>
            <p className="mt-3 text-sm text-slate-500">
              Didn't get it? Check spam, or wait a minute and{" "}
              <button
                type="button"
                onClick={() => setSent(false)}
                className="font-medium text-slate-900 underline"
              >
                try again
              </button>
              .
            </p>
            <p className="mt-6 text-sm text-slate-600">
              <Link to="/login" className="font-medium text-slate-900 underline">
                Back to sign in
              </Link>
            </p>
          </>
        ) : (
          <>
            <p className="mt-2 text-slate-600">
              Enter your email and we'll send you a link to set a new password.
            </p>
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700" htmlFor="email">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 p-3"
                  required
                  autoComplete="email"
                />
              </div>

              {error ? (
                <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">
                  {error}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={submitting || !email.trim()}
                className="no-tap-highlight w-full rounded-lg bg-slate-900 p-3 font-medium text-white disabled:opacity-50"
              >
                {submitting ? "Sending…" : "Send reset link"}
              </button>
            </form>
            <p className="mt-6 text-sm text-slate-600">
              Remembered it?{" "}
              <Link to="/login" className="font-medium text-slate-900 underline">
                Back to sign in
              </Link>
            </p>
          </>
        )}
      </div>
    </main>
  );
}
